import { useEffect } from 'react'

interface WakeLockSentinelLike {
  release: () => Promise<void>
  addEventListener: (type: 'release', listener: () => void) => void
}

interface WakeLockApiLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>
}

// Keep the screen awake while this hook is mounted. No-ops on browsers without
// the Wake Lock API (iOS < 16.4, Firefox). The lock auto-releases when the page
// is hidden and is re-acquired on visibility change so we don't hold the
// battery captive after the user leaves the tab.
export function useWakeLock() {
  useEffect(() => {
    const nav = navigator as Navigator & { wakeLock?: WakeLockApiLike }
    if (!nav.wakeLock) return

    let sentinel: WakeLockSentinelLike | null = null
    let cancelled = false

    const acquire = async () => {
      if (cancelled) return
      try {
        const next = await nav.wakeLock!.request('screen')
        if (cancelled) {
          next.release().catch(() => {})
          return
        }
        sentinel = next
        sentinel.addEventListener('release', () => {
          sentinel = null
        })
      } catch (err) {
        console.debug('[wake-lock] acquire failed', err)
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !sentinel) acquire()
    }

    acquire()
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibility)
      sentinel?.release().catch(() => {})
    }
  }, [])
}
